import type { ImageCropBox } from "@/components/ui/image-cropper/ImageCropper";
import type { TemplateLayoutSpec } from "@/types/template-layout/templateLayout.types";
import { loadImageSize } from "./bannerSizeTip";
import { resolveLayoutAssetPreviewUrl } from "./layoutAssetPreviewUrl";
import { isSameLayoutImage, layoutImageAssetId } from "./layoutImageAssets";
import {
  cropGraphQLTemplateLayoutAsset,
  ingestGraphQLTemplateLayoutAssetFromUrl,
} from "./repository/graphqlTemplateLayoutAssetRepository";

/** Which image a chat request names: the banner, a body image, or "it"/nothing. */
export type ChatImageEditTarget = "banner" | "image" | "any";

export type ChatImageEditAction =
  /** Crop to a shape, as width ÷ height. */
  | { kind: "shape"; aspect: number; label: string }
  /** Resize to a pixel size; with both sides, crop to that shape first. */
  | { kind: "size"; width?: number; height?: number }
  /** Shrink to this fraction of the size the image shows at. */
  | { kind: "smaller"; scale: number }
  | { kind: "bigger" }
  /** A crop the chat can't work out itself, such as "crop to the people". */
  | { kind: "manual" };

export interface ChatImageEditRequest {
  target: ChatImageEditTarget;
  action: ChatImageEditAction;
  /** True when the message asks for nothing else, so it needs no layout change. */
  standalone: boolean;
}

const BANNER_NOUN =
  /\b(?:banners?|(?:header|top|hero)\s+(?:images?|photos?|pictures?))\b/;
const IMAGE_NOUN = /\b(?:images?|photos?|pictures?|pics?)\b/;
const PRONOUN = /\b(?:it|this|that)\b/;
/** Words that put the request on something next to the image, such as "the banner text". */
const NOT_THE_IMAGE =
  /\b(?:text|titles?|headings?|headlines?|captions?|fonts?|copy|labels?|alt|logos?|charts?|tables?|sections?|padding|margins?|spacing|gaps?|borders?|corners?|radius|shadows?|header(?!\s+(?:images?|photos?|pictures?)))\b/;
/** Layout changes that mention an image, such as "use this as a banner image". */
const LAYOUT_VERB =
  /\b(?:add|insert|upload|use|replace|swap|remove|delete|move|attach|put|place)\b/;
const CROP_VERB = /\b(?:crop|crops|cropped|cropping|trim|trimmed|trimming)\b/;
const RESIZE_VERB = /\b(?:re-?siz(?:e|es|ed|ing)|cut)\b/;
const NUMBER = String.raw`(\d[\d,]*(?:\.\d+)?)`;
const PIXELS = String.raw`\s*(?:px|pixels?)`;
const PAIR = new RegExp(
  `${NUMBER}(${PIXELS})?\\s*(:|\\bby\\b|x|×)\\s*${NUMBER}(${PIXELS})?`,
);
const WIDTH = new RegExp(
  `${NUMBER}${PIXELS}\\s*(?:wide|across|in width|width)\\b|\\bwidth\\s*(?:of|to|:|=)?\\s*${NUMBER}`,
);
const HEIGHT = new RegExp(
  `${NUMBER}${PIXELS}\\s*(?:tall|high|in height|height)\\b|\\bheight\\s*(?:of|to|:|=)?\\s*${NUMBER}`,
);
const SQUARE = /\bsquare\b/;
const BANNER_SHAPE =
  /\bbanner[- ](?:size|shape|ratio|dimensions?|format)\b|\bfit\b.*\bbanner\b|\b(?:crop|trim)\w*\s+(?:(?:it|this|that)\s+)?(?:in)?to\s+(?:a\s+|the\s+)?banner\b/;
const SMALLER =
  /\b(?:smaller|shrink|shrunk|downsize|tinier|shorter|less\s+tall|half\s+(?:the|its)\s+size|reduce\s+(?:the\s+|its\s+)?size|scale\s+(?:\w+\s+){0,2}down)\b/;
const BIGGER =
  /\b(?:bigger|larger|enlarge|upscale|taller|increase\s+(?:the\s+|its\s+)?size|scale\s+(?:\w+\s+){0,2}up|blow\s+(?:\w+\s+)?up)\b/;

/** A message's clauses, so "crop the banner, and make the title bigger" is two requests. */
const CLAUSE_BREAK = /[.;!?]+(?:\s+|$)|\n+|,\s+|\s+(?:and|then|also|plus)\s+/;

const toNumber = (text: string | undefined) =>
  text == null ? undefined : Number(text.replaceAll(",", ""));

const readScale = (clause: string) => {
  if (/\bhalf\b/.test(clause)) return 0.5;
  const percent = toNumber(/(\d+(?:\.\d+)?)\s*%/.exec(clause)?.[1]);
  if (percent != null && percent > 0 && percent < 100) {
    return /%\s*smaller\b/.test(clause) ? 1 - percent / 100 : percent / 100;
  }
  if (/\b(?:a\s+(?:bit|little|touch)|slightly)\b/.test(clause)) return 0.85;
  return 0.75;
};

/** The action a clause asks for, and whether it is image-only wording that needs no image noun. */
const readAction = (
  clause: string,
): { action: ChatImageEditAction; imageOnly: boolean } | null => {
  const pair = PAIR.exec(clause);
  if (pair != null) {
    const [, first, firstUnit, separator, second, secondUnit] = pair;
    const a = toNumber(first) ?? 0;
    const b = toNumber(second) ?? 0;
    if (a > 0 && b > 0) {
      const isPixelSize =
        separator !== ":" &&
        (firstUnit != null || secondUnit != null || a > 32 || b > 32);
      return isPixelSize
        ? { action: { kind: "size", width: a, height: b }, imageOnly: false }
        : {
            action: { kind: "shape", aspect: a / b, label: `${a}:${b}` },
            imageOnly: true,
          };
    }
  }
  if (BANNER_SHAPE.test(clause)) {
    return {
      action: { kind: "shape", aspect: 3, label: "the banner shape (3:1)" },
      imageOnly: true,
    };
  }
  if (SQUARE.test(clause)) {
    return {
      action: { kind: "shape", aspect: 1, label: "a square" },
      imageOnly: true,
    };
  }
  const width = WIDTH.exec(clause);
  if (width != null) {
    return {
      action: { kind: "size", width: toNumber(width[1] ?? width[2]) },
      imageOnly: false,
    };
  }
  const height = HEIGHT.exec(clause);
  if (height != null) {
    return {
      action: { kind: "size", height: toNumber(height[1] ?? height[2]) },
      imageOnly: false,
    };
  }
  if (SMALLER.test(clause)) {
    return {
      action: { kind: "smaller", scale: readScale(clause) },
      imageOnly: false,
    };
  }
  if (BIGGER.test(clause)) {
    return { action: { kind: "bigger" }, imageOnly: false };
  }
  if (CROP_VERB.test(clause)) {
    return { action: { kind: "manual" }, imageOnly: true };
  }
  if (RESIZE_VERB.test(clause)) {
    return { action: { kind: "manual" }, imageOnly: false };
  }
  return null;
};

const readClause = (
  clause: string,
): Omit<ChatImageEditRequest, "standalone"> | null => {
  if (NOT_THE_IMAGE.test(clause) || LAYOUT_VERB.test(clause)) return null;
  const read = readAction(clause);
  if (read == null) return null;
  const { action } = read;
  const namesBanner = BANNER_NOUN.test(clause);
  const namesImage = IMAGE_NOUN.test(clause.replace(BANNER_NOUN, ""));
  // "Fit the photo to the banner" crops the photo; "fit it to the banner" crops the banner.
  if (namesImage) return { target: "image", action };
  if (namesBanner) return { target: "banner", action };
  // "Crop it to 16:9" can only mean an image; "make it smaller" could mean anything.
  if (!read.imageOnly) return null;
  if (PRONOUN.test(clause) || CROP_VERB.test(clause)) {
    return { target: "any", action };
  }
  return null;
};

/**
 * The crop or resize a chat message asks for, when it names an image (or says "crop"); null
 * for anything else, such as "make the banner text bigger". Only the first such clause counts.
 */
export const parseChatImageEditRequest = (
  prompt: string,
): ChatImageEditRequest | null => {
  const clauses = prompt
    .toLowerCase()
    .split(CLAUSE_BREAK)
    .map((clause) => clause.trim())
    .filter((clause) => clause !== "");
  for (const [index, clause] of clauses.entries()) {
    const request = readClause(clause);
    if (request != null) {
      return { ...request, standalone: clauses.length === 1 && index === 0 };
    }
  }
  return null;
};

export interface LayoutImage {
  src: string;
  alt: string;
  banner: boolean;
}

/** The distinct Images in the layout, top to bottom; two URL forms of one asset count once. */
export const listLayoutImages = (
  spec: Pick<TemplateLayoutSpec, "root" | "elements">,
): LayoutImage[] => {
  const images: LayoutImage[] = [];
  const visit = (id: string, seen: Set<string>) => {
    const element = spec.elements[id];
    if (element == null || seen.has(id)) return;
    seen.add(id);
    const src = element.props?.src;
    if (element.type === "Image" && typeof src === "string" && src !== "") {
      const banner = element.props?.banner === true;
      const known = images.find((image) => isSameLayoutImage(image.src, src));
      if (known == null) {
        images.push({ src, alt: String(element.props?.alt ?? ""), banner });
      } else if (banner) {
        known.banner = true;
      }
    }
    for (const child of element.children ?? []) visit(child, seen);
  };
  visit(spec.root, new Set());
  return images;
};

export type ChatImageEditTargetResult =
  | { kind: "image"; image: LayoutImage }
  | { kind: "choose"; images: LayoutImage[] }
  | { kind: "none" };

/**
 * The image a request means. "Banner" is the banner and "photo" or "image" the body image, or
 * the only image when there is just one; otherwise the user chooses.
 */
export const resolveChatImageEditTarget = (
  images: readonly LayoutImage[],
  target: ChatImageEditTarget,
): ChatImageEditTargetResult => {
  const [only] = images;
  if (only == null) return { kind: "none" };
  if (images.length === 1) return { kind: "image", image: only };
  const named =
    target === "banner"
      ? images.filter((image) => image.banner)
      : target === "image"
        ? images.filter((image) => !image.banner)
        : [...images];
  const [image] = named;
  if (image != null && named.length === 1) return { kind: "image", image };
  return { kind: "choose", images: named.length > 1 ? named : [...images] };
};

/** The narrowest or shortest the chat makes an image, in pixels; the crop dialog's Width stops here too. */
const MIN_SIDE_PX = 40;

interface ImageSize {
  width: number;
  height: number;
}

export type ChatImageEditPlan =
  /** Fractions of the image, and a smaller width to scale the crop to. */
  | {
      kind: "crop";
      edit: ImageCropBox & { targetWidth?: number };
      message: string;
    }
  /** Nothing to edit; tell the user why. */
  | { kind: "explain"; message: string }
  /** Open the crop dialog so the user draws the box. */
  | { kind: "manual"; message: string }
  /** The layout, not the file, sets how big it shows: leave it to Herald. */
  | { kind: "layout" };

const NO_UPSCALE =
  "I don't scale images up, because that makes them blurry. Upload a larger image to make it bigger.";

/** A centred whole-pixel box of `aspect` (width ÷ height), as fractions naapi turns back into the same pixels. */
const centredBox = ({ width, height }: ImageSize, aspect: number) => {
  const cropWidth = Math.min(width, Math.round(height * aspect));
  const cropHeight = Math.min(height, Math.round(cropWidth / aspect));
  const left = Math.floor((width - cropWidth) / 2);
  const top = Math.floor((height - cropHeight) / 2);
  return {
    box: {
      left: left / width,
      top: top / height,
      width: cropWidth / width,
      height: cropHeight / height,
    },
    cropWidth,
    cropHeight,
  };
};

const isAspect = ({ width, height }: ImageSize, aspect: number) =>
  Math.abs(width / height - aspect) <= aspect * 0.01;

const scaledHeight = (size: ImageSize, width: number) =>
  Math.max(1, Math.round((size.height * width) / size.width));

/**
 * The crop or resize that does what the chat asked, from the image's own size and, for body
 * Images, the width it shows at in the preview. Banners always fill the page width, so they
 * only crop; nothing is ever scaled up.
 */
export const planChatImageEdit = ({
  action,
  banner,
  size,
  shownWidth,
}: {
  action: ChatImageEditAction;
  banner: boolean;
  size: ImageSize;
  /** CSS pixels the body Image takes in the preview, when known. */
  shownWidth?: number;
}): ChatImageEditPlan => {
  const name = banner ? "the banner" : "the image";
  const Name = banner ? "The banner" : "The image";
  const sizeLabel = (width: number, height: number) =>
    `${width} × ${height} px`;

  switch (action.kind) {
    case "manual":
      return {
        kind: "manual",
        message: `I opened the crop tool on ${name}. Drag the box around the part to keep, then select ${banner ? "Crop image" : "Apply"}.`,
      };
    case "shape": {
      if (isAspect(size, action.aspect)) {
        return {
          kind: "explain",
          message: `${Name} is already ${action.label}.`,
        };
      }
      const { box, cropWidth, cropHeight } = centredBox(size, action.aspect);
      return {
        kind: "crop",
        edit: box,
        message: `Cropped ${name} to ${action.label}, ${sizeLabel(cropWidth, cropHeight)}.`,
      };
    }
    case "size": {
      if (banner) {
        return {
          kind: "explain",
          message:
            "The banner always fills the page width, so a pixel size doesn't change how it shows. Ask for a shape instead, like 3:1 or 16:9.",
        };
      }
      const { width, height } = action;
      const crop =
        width != null && height != null && !isAspect(size, width / height)
          ? centredBox(size, width / height)
          : null;
      const from = crop
        ? { width: crop.cropWidth, height: crop.cropHeight }
        : size;
      const targetWidth =
        width ??
        (height != null
          ? Math.round((height * from.width) / from.height)
          : from.width);
      if (targetWidth > from.width) {
        return {
          kind: "explain",
          message: `${Name} is only ${sizeLabel(from.width, from.height)}. ${NO_UPSCALE}`,
        };
      }
      if (targetWidth < MIN_SIDE_PX) {
        return {
          kind: "explain",
          message: `That's too small. The smallest the image can be is ${MIN_SIDE_PX} px wide.`,
        };
      }
      if (crop == null && targetWidth === size.width) {
        return {
          kind: "explain",
          message: `${Name} is already ${sizeLabel(size.width, size.height)}.`,
        };
      }
      return {
        kind: "crop",
        edit: {
          ...(crop?.box ?? { left: 0, top: 0, width: 1, height: 1 }),
          ...(targetWidth < from.width ? { targetWidth } : {}),
        },
        message: `Resized the image to ${sizeLabel(targetWidth, scaledHeight(from, targetWidth))}.`,
      };
    }
    case "smaller": {
      if (banner) {
        // A banner is as wide as the page, so smaller means shorter: first the 3:1 banner shape.
        const aspect =
          size.width / size.height < 3 * 0.99
            ? 3
            : size.width / Math.round(size.height * action.scale);
        const { box, cropWidth, cropHeight } = centredBox(size, aspect);
        if (cropHeight < MIN_SIDE_PX) {
          return {
            kind: "explain",
            message: `The banner is only ${size.height} px tall, so it can't get any shorter.`,
          };
        }
        return {
          kind: "crop",
          edit: box,
          message: `Made the banner shorter by cropping it to ${sizeLabel(cropWidth, cropHeight)}. A banner always fills the page width, so it can't get narrower.`,
        };
      }
      // Shrinking a large file that already shows scaled down would change nothing on the page.
      const base = Math.min(size.width, shownWidth || size.width);
      const targetWidth = Math.round(base * action.scale);
      if (targetWidth < MIN_SIDE_PX) {
        return {
          kind: "explain",
          message: `The image is already as small as it can be, ${MIN_SIDE_PX} px wide.`,
        };
      }
      return {
        kind: "crop",
        edit: { left: 0, top: 0, width: 1, height: 1, targetWidth },
        message: `Made the image smaller: ${sizeLabel(targetWidth, scaledHeight(size, targetWidth))}.`,
      };
    }
    case "bigger": {
      if (banner) {
        return {
          kind: "explain",
          message:
            "The banner already fills the page width. It can only get taller with a taller image: upload one, or undo an earlier crop.",
        };
      }
      if (shownWidth && shownWidth < size.width - 1) return { kind: "layout" };
      return {
        kind: "explain",
        message: `The image already shows at its full size, ${sizeLabel(size.width, size.height)}. ${NO_UPSCALE}`,
      };
    }
  }
};

export type ChatImageEditOutcome =
  | Exclude<ChatImageEditPlan, { kind: "crop" }>
  /** The edited copy is stored at `url`; the source is unchanged. */
  | { kind: "edited"; url: string; message: string }
  | { kind: "failed"; message: string };

/**
 * Plans the edit from the image's size and runs a crop on a copy with naapi; a remote image is
 * copied into an asset first. Makes no model call.
 */
export const runChatImageEdit = async ({
  action,
  image,
  shownWidth,
  signal,
}: {
  action: ChatImageEditAction;
  image: LayoutImage;
  shownWidth?: number;
  signal: AbortSignal;
}): Promise<ChatImageEditOutcome> => {
  const size =
    action.kind === "manual"
      ? { width: 1, height: 1 }
      : await loadImageSize(
          resolveLayoutAssetPreviewUrl(image.src),
          AbortSignal.any([signal, AbortSignal.timeout(10_000)]),
        );
  if (size == null) {
    return {
      kind: "failed",
      message: "Couldn't load the image to measure it.",
    };
  }
  const plan = planChatImageEdit({
    action,
    banner: image.banner,
    size,
    shownWidth,
  });
  if (plan.kind !== "crop") return plan;
  const storedAssetId = layoutImageAssetId(image.src);
  const source =
    storedAssetId != null
      ? { success: true as const, data: { assetId: storedAssetId } }
      : await ingestGraphQLTemplateLayoutAssetFromUrl(
          resolveLayoutAssetPreviewUrl(image.src),
        );
  const result = source.success
    ? await cropGraphQLTemplateLayoutAsset(
        source.data.assetId,
        plan.edit,
        signal,
      )
    : source;
  return result.success
    ? { kind: "edited", url: result.data.url, message: plan.message }
    : {
        kind: "failed",
        message: `Couldn't edit the image: ${result.error.message}`,
      };
};
